<template>
  <div class="fault-analysis-report">
    <!-- 导航提示标题 -->
    <ml-tip :showHeader="false" content="故障分析报表" />

    <!-- 搜索框 -->
    <ml-tip :showHeader="false" style="border: 0">
      <ml-form inline labelWidth="100px" :model="searchData" ref="searchDataRef">
        <!-- 阿米巴 -->
        <ml-select
          prop="companyId"
          placeholder="请选择阿米巴"
          :options="unitOptions"
          style="margin-bottom: 10px"
          label="阿米巴:"
          keyName="companyId"
          labelName="companyName"
          valueName="companyId"
          v-model="searchData.companyId"
          @change="selectUnit"
        />

        <!-- 所属项目 -->
        <ml-select
          prop="projectId"
          placeholder="请选择所属项目"
          :options="projectOptions"
          style="margin-bottom: 10px"
          label="所属项目:"
          keyName="projectId"
          labelName="projectName"
          valueName="projectId"
          v-model="searchData.projectId"
        />

        <!-- 选择月份 -->
        <ml-date-picker
          prop="reportMonth"
          type="month"
          placeholder="请选择月份"
          label="选择月份:"
          valueFormat="YYYY-MM"
          format="YYYY年MM月"
          :clearable="false"
          v-model="searchData.reportMonth"
          style="margin-bottom: 10px"
        />

        <!-- 故障类型 -->
        <ml-select
          prop="errorType"
          placeholder="请选择故障类型"
          :options="errorTypeOptions"
          style="margin-bottom: 10px"
          label="故障类型:"
          keyName="value"
          labelName="label"
          valueName="value"
          v-model="searchData.errorType"
        />

        <!-- 搜索按钮 -->
        <ml-button
          :style="{ 'margin-bottom': 0 }"
          submitText="查询"
          submitIcon="el-icon-search"
          cancelText="重置"
          cancelIcon="el-icon-refresh"
          cancelType="danger"
          @click-cancel="resetSearchData"
          @click-submit="searchFn"
        />
      </ml-form>
    </ml-tip>

    <!-- 统计概览区 -->
    <ml-tip :showHeader="false" style="border: 0; margin-bottom: 20px">
      <div class="statistics-cards">
        <!-- 故障总数 -->
        <div class="stat-card">
          <div class="stat-title">故障总数</div>
          <div class="stat-value">{{ statisticsData.count || 0 }}</div>
          <div class="stat-compare" :class="getCompareClass(statisticsData.lastRate)">
            较上月{{ statisticsData.lastRate > 0 ? '增长' : '减少' }}
            {{ formatRate(statisticsData.lastRate) }}
          </div>
        </div>

        <!-- 高频故障类型 -->
        <div class="stat-card">
          <div class="stat-title">高频故障类型</div>
          <div class="stat-value highlight">
            {{ (statisticsData.maxErrorType && statisticsData.maxErrorType.errorTypeName) || '-' }}
          </div>
          <div class="stat-compare">
            占比:
            {{
              statisticsData.maxErrorType && statisticsData.maxErrorType.rate
                ? statisticsData.maxErrorType.rate + '%'
                : '-'
            }}
          </div>
        </div>

        <!-- 平均处理时长 -->
        <div class="stat-card">
          <div class="stat-title">平均处理时长</div>
          <div class="stat-value">
            {{ statisticsData.avgHours ? statisticsData.avgHours + '小时' : '0小时' }}
          </div>
          <div class="stat-compare" :class="getCompareClass(statisticsData.lastAvgHoursRate, true)">
            效率: 较上月{{
              statisticsData.lastAvgHoursRate
                ? statisticsData.lastAvgHoursRate > 0
                  ? '提高'
                  : '降低'
                : '-'
            }}
            {{ Math.abs(statisticsData.lastAvgHoursRate || 0) }}%
          </div>
        </div>

        <!-- 故障最多设备类型 -->
        <div class="stat-card">
          <div class="stat-title">故障最多设备类型</div>
          <div class="stat-value highlight">{{ statisticsData.device || '-' }}</div>
          <div class="stat-compare">故障次数: {{ statisticsData.deviceErrorCount || 0 }}次</div>
        </div>
      </div>
    </ml-tip>

    <!-- 故障类型分析和详情统计 -->
    <el-row :gutter="20" style="margin-bottom: 20px">
      <!-- 故障类型分析饼图 -->
      <el-col :span="12">
        <ml-tip :showHeader="false" style="border: 0">
          <div class="chart-header">
            <div class="chart-title">
              <div class="title-border"></div>
              <div class="title-text">{{ searchData.reportMonth }} 故障类型分析</div>
            </div>
            <div class="chart-update-time">数据更新时间: {{ updateTime }}</div>
          </div>
          <div class="chart-container">
            <ml-echarts
              id="faultTypePieChart"
              :option="pieChartOption"
              style="width: 100%; height: 350px"
            />
          </div>
        </ml-tip>
      </el-col>

      <!-- 故障类型详情统计 -->
      <el-col :span="12">
        <ml-tip :showHeader="false" style="border: 0">
          <div class="chart-header">
            <div class="chart-title">
              <div class="title-border"></div>
              <div class="title-text">故障类型详情统计</div>
            </div>
          </div>
          <el-table :data="errorTypeDetailData" style="width: 100%" :height="350">
            <el-table-column
              align="center"
              label="故障类型"
              prop="errorTypeName"
              min-width="150px"
              show-overflow-tooltip
            />
            <el-table-column align="center" label="故障数量" prop="count" min-width="100px" />
            <el-table-column align="center" label="占比" prop="rate" min-width="100px">
              <template #default="scope">
                {{ scope.row.rate ? scope.row.rate + '%' : '-' }}
              </template>
            </el-table-column>
            <el-table-column align="center" label="平均处理时长" prop="avgHours" min-width="130px">
              <template #default="scope">
                {{ scope.row.avgHours ? scope.row.avgHours + '小时' : '-' }}
              </template>
            </el-table-column>
            <el-table-column
              align="center"
              label="最高频设备"
              prop="device"
              min-width="150px"
              show-overflow-tooltip
            />
          </el-table>
        </ml-tip>
      </el-col>
    </el-row>

    <!-- 故障详情列表 -->
    <ml-tip :showHeader="false" style="border: 0">
      <div class="chart-header">
        <div class="chart-title">
          <div class="title-border"></div>
          <div class="title-text">故障详情列表</div>
        </div>
        <div>
          <el-input
            v-model="detailSearchKeyword"
            placeholder="搜索设备/故障类型/项目"
            prefix-icon="el-icon-search"
            style="width: 300px"
            clearable
            @clear="handleDetailSearch"
            @keyup.enter="handleDetailSearch"
          >
          </el-input>
          <el-button type="primary" @click="handleDetailSearch" style="margin-left: 10px">查询</el-button>
        </div>
      </div>

      <el-table :data="tabberData.data" style="width: 100%" :height="tabberHeight(310)">
        <el-table-column align="center" type="index" width="80">
          <template #default="scope">
            {{ (tabberData.page - 1) * tabberData.size + (scope.$index + 1) }}
          </template>
        </el-table-column>

        <el-table-column
          align="center"
          label="故障ID"
          prop="errorId"
          min-width="100px"
          show-overflow-tooltip
        />

        <el-table-column
          align="center"
          label="所属项目"
          prop="projectName"
          min-width="120px"
          show-overflow-tooltip
        />

        <el-table-column
          align="center"
          label="所属单位"
          prop="companyName"
          min-width="120px"
          show-overflow-tooltip
        />

        <el-table-column
          align="center"
          label="设备名称"
          prop="deviceName"
          min-width="120px"
          show-overflow-tooltip
        />

        <el-table-column
          align="center"
          label="故障类型"
          prop="errorTypeName"
          min-width="150px"
          show-overflow-tooltip
        />

        <el-table-column
          align="center"
          label="故障时间"
          prop="submitTime"
          min-width="160px"
          show-overflow-tooltip
        />

        <el-table-column align="center" label="处理状态" prop="errorStatus" min-width="100px">
          <template #default="scope">
            <el-tag :type="getStatusType(scope.row.errorStatus)" size="small">
              {{ getStatusText(scope.row.errorStatus) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column align="center" label="处理时长" prop="hours" min-width="100px">
          <template #default="scope">
            {{ scope.row.hours ? scope.row.hours + '小时' : '-' }}
          </template>
        </el-table-column>

        <!-- <el-table-column align="center" label="操作" min-width="150px" fixed="right">
          <template #default="scope">
            <el-button
              size="mini"
              type="primary"
              icon="el-icon-view"
              @click="handleView(scope.row)"
            >
              查看
            </el-button>
            <el-button
              size="mini"
              type="warning"
              icon="el-icon-time"
              @click="handleViewRecord(scope.row)"
            >
              处理记录
            </el-button>
          </template>
        </el-table-column> -->
      </el-table>

      <!-- 分页 -->
      <ml-pagination
        :total="tabberData.total"
        :page="tabberData.page"
        v-model="tabberData.page"
        :size="tabberData.size"
        @current-change="currentChange"
        @size-change="sizeChange"
      />
    </ml-tip>
  </div>
</template>

<script>
import { onMounted, reactive, ref, watch } from 'vue'
import { tabberHeight, getTabberData } from '@/utils'
import { useStore } from 'vuex'

export default {
  name: 'FaultAnalysisReport',
  setup() {
    const { dispatch, getters } = useStore()

    // 获取是否是商务部
    const isBusinessDepartment = getters.isBusinessDepartment

    // 获取当前年月
    const getCurrentMonth = () => {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      return `${year}-${month}`
    }

    // 更新时间
    const updateTime = ref('')

    /* 开始 搜索 **********************************/
    const searchDataRef = ref()
    const searchData = reactive({
      companyId: '',
      projectId: '',
      reportMonth: getCurrentMonth(),
      errorType: ''
    })

    // 阿米巴列表
    const unitOptions = ref([])
    // 项目列表
    const projectOptions = ref([])
    // 故障类型列表
    const errorTypeOptions = ref([
      { value: '', label: '全部' },
      { value: '0', label: '待处理' },
      { value: '1', label: '已处理' },
      { value: '3', label: '已完结' }
    ])

    const getStatusText = status => {
      switch (status) {
        case '0':
          return '待处理'
        case '1':
          return '已处理'
        case '3':
          return '已完结'
        default:
          return '-'
      }
    }

    // 根据阿米巴获取项目列表
    const getProjectData = async companyId => {
      const projectParams = {}
      if (isBusinessDepartment && companyId) {
        projectParams.companyId = companyId
      }
      const { data } = await getTabberData(dispatch, 'fetchGetProjectsByCompany', projectParams)
      projectOptions.value = data || []
    }

    // 选择阿米巴
    const selectUnit = () => {
      searchData.projectId = ''
      getProjectData(searchData.companyId)
      searchFn()
    }

    // 重置搜索
    const resetSearchData = () => {
      searchDataRef.value.CustomFormRef.resetFields()
      searchData.companyId = ''
      searchData.projectId = ''
      searchData.reportMonth = getCurrentMonth()
      searchData.errorType = ''
      tabberData.page === 1 ? loadData() : (tabberData.page = 1)
    }

    // 搜索
    const searchFn = () => {
      tabberData.page === 1 ? loadData() : (tabberData.page = 1)
    }
    /* 结束 搜索 **********************************/

    /* 开始 统计数据 **********************************/
    const statisticsData = reactive({
      count: 0,
      lastRate: null,
      maxErrorType: null,
      maxErrorTypeRate: null,
      avgHours: 0,
      lastAvgHoursRate: null,
      device: '',
      deviceErrorCount: 0,
      errorTypeList: []
    })

    // 格式化比率
    const formatRate = rate => {
      if (rate === null || rate === undefined) return '-'
      return Math.abs(rate) + '%'
    }

    // 获取对比样式类
    const getCompareClass = (rate, isInverse = false) => {
      if (rate === null || rate === undefined) return ''
      if (isInverse) {
        return rate > 0 ? 'text-success' : 'text-danger'
      }
      return rate > 0 ? 'text-success' : 'text-danger'
    }
    /* 结束 统计数据 **********************************/

    /* 开始 饼图配置 **********************************/
    const pieChartOption = ref({})

    // 更新饼图配置
    const updatePieChart = data => {
      if (!data || data.length === 0) {
        pieChartOption.value = {}
        return
      }

      pieChartOption.value = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: '5%',
          top: 'center',
          textStyle: {
            fontSize: 12
          }
        },
        color: [
          '#5470c6',
          '#91cc75',
          '#fac858',
          '#ee6666',
          '#73c0de',
          '#3ba272',
          '#fc8452',
          '#9a60b4'
        ],
        series: [
          {
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['40%', '50%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 5,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 14,
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: data.map(item => ({
              name: item.name,
              value: item.value
            }))
          }
        ]
      }
    }
    /* 结束 饼图配置 **********************************/

    /* 开始 故障类型详情统计 **********************************/
    const errorTypeDetailData = ref([])
    /* 结束 故障类型详情统计 **********************************/

    /* 开始 故障详情列表 **********************************/
    const detailSearchKeyword = ref('')

    const tabberData = reactive({
      data: [],
      total: 0,
      page: 1,
      size: 10
    })

    // 监听分页变化
    watch([() => tabberData.page, () => tabberData.size], () => {
      getDetailTableData()
    })

    // 详情搜索
    const handleDetailSearch = () => {
      tabberData.page = 1
      getDetailTableData()
    }

    // 获取故障详情列表数据
    const getDetailTableData = async () => {
      const params = {
        reportMonth: searchData.reportMonth || '',
        keyword: detailSearchKeyword.value || '',
        companyId: searchData.companyId || '',
        projectId: searchData.projectId || '',
        errorType: searchData.errorType || '',
        pageIndex: tabberData.page,
        pageSize: tabberData.size
      }
      const { data } = await getTabberData(dispatch, 'fetchGetMonthAnalyzePage', params)
      tabberData.data = data?.records || []
      tabberData.total = data?.total || 0
    }

    // 分页事件
    const currentChange = page => {
      tabberData.page = page
    }
    const sizeChange = size => {
      tabberData.size = size
      tabberData.page = 1
    }

    // 获取状态类型
    const getStatusType = status => {
      if (!status) return 'info'
      if (status.includes('1') || status.includes('3')) return 'success'
      if (status.includes('0')) return 'warning'
      return 'info'
    }

    // 查看
    const handleView = row => {
      console.log('查看', row)
      // TODO: 实现查看功能
    }

    // 查看处理记录
    const handleViewRecord = row => {
      console.log('查看处理记录', row)
      // TODO: 实现查看处理记录功能
    }
    /* 结束 故障详情列表 **********************************/

    /* 开始 导出数据 **********************************/
    const clickExport = () => {
      // TODO: 实现导出功能
      console.log('导出')
    }
    /* 结束 导出数据 **********************************/

    /* 开始 加载数据 **********************************/
    const loadData = async () => {
      // 加载统计数据
      const countParams = {
        reportMonth: searchData.reportMonth || '',
        keyword: '',
        companyId: searchData.companyId || '',
        projectId: searchData.projectId || '',
        errorType: searchData.errorType || '',
        pageIndex: 1,
        pageSize: 10
      }
      const { data: countData } = await getTabberData(
        dispatch,
        'fetchGetMonthAnalyzeCount',
        countParams
      )

      if (countData) {
        statisticsData.count = countData.count || 0
        statisticsData.lastRate = countData.lastRate
        statisticsData.maxErrorType = countData.maxErrorType
        statisticsData.avgHours = countData.avgHours || 0
        statisticsData.lastAvgHoursRate = countData.lastAvgHoursRate
        statisticsData.device = countData.device || ''
        statisticsData.deviceErrorCount = countData.deviceErrorCount || 0
        errorTypeDetailData.value = countData.errorTypeList || []

        // 更新饼图数据
        if (countData.errorTypeList && countData.errorTypeList.length > 0) {
          const pieData = countData.errorTypeList.map(item => ({
            name: item.errorTypeName || '--',
            value: item.count || 1
          }))
          updatePieChart(pieData)
        } else {
          updatePieChart([])
        }
      }

      // 更新时间
      const now = new Date()
      updateTime.value = `${now.getFullYear()}/${String(now.getMonth() + 1).padStart(
        2,
        '0'
      )}/${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(
        2,
        '0'
      )}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`

      // 加载故障详情列表
      getDetailTableData()
    }
    /* 结束 加载数据 **********************************/

    onMounted(async () => {
      // 获取阿米巴列表
      const { data: companyData } = await getTabberData(dispatch, 'fetchGetCompanyInfos', {})
      unitOptions.value = companyData || []

      // 获取项目列表（根据阿米巴筛选）
      getProjectData(searchData.companyId)

      // 加载数据
      loadData()
    })

    return {
      searchDataRef,
      searchData,
      unitOptions,
      projectOptions,
      errorTypeOptions,
      selectUnit,
      resetSearchData,
      searchFn,
      statisticsData,
      formatRate,
      getCompareClass,
      pieChartOption,
      errorTypeDetailData,
      detailSearchKeyword,
      tabberData,
      currentChange,
      sizeChange,
      tabberHeight,
      getStatusType,
      handleView,
      handleViewRecord,
      handleDetailSearch,
      clickExport,
      updateTime,
      getStatusText
    }
  }
}
</script>

<style lang="scss" scoped>
.fault-analysis-report {
  @extend %params-global;
}

.statistics-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  .stat-card {
    background: #f5f7fa;
    border-radius: 8px;
    padding: 20px;
    text-align: center;

    .stat-title {
      font-size: 14px;
      color: #909399;
      margin-bottom: 10px;
    }

    .stat-value {
      font-size: 28px;
      font-weight: bold;
      color: #303133;
      margin-bottom: 8px;

      &.highlight {
        font-size: 20px;
      }
    }

    .stat-compare {
      font-size: 12px;
      color: #909399;

      &.text-danger {
        color: #f56c6c;
      }

      &.text-success {
        color: #67c23a;
      }
    }
  }
}

.chart-header {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .chart-title {
    display: flex;
    align-items: center;
  }
  .title-border {
    width: 6px;
    height: 16px;
    border-radius: 2px;
    background: #409eff;
    margin-right: 8px;
  }
  .title-text {
    font-size: 16px;
    font-weight: bold;
    color: #303133;
  }

  .chart-subtitle {
    font-size: 14px;
    color: #606266;
    margin-bottom: 5px;
  }

  .chart-update-time {
    font-size: 12px;
    color: #909399;
  }
}

.chart-container {
  width: 100%;
  height: 350px;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;

  .detail-title {
    font-size: 16px;
    font-weight: bold;
    color: #303133;

    i {
      margin-right: 5px;
    }
  }
}
</style>
